import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Wallet, Loader2, ExternalLink, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fmt = (v) =>
  v != null && !isNaN(v)
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v)
    : "-";

const fmtDec = (v, digits = 2) =>
  v != null && !isNaN(v)
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v)
    : "-";

/**
 * Dashboard "Coût par véhicule" — tableau consolidé de tous les véhicules
 * avec la décomposition mensuelle par type de contrat (leasing/assurance/entretien/autre)
 * et le coût / km si l'info est disponible.
 */
export default function VehiclesCostsTable({ token }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fleet/costs-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error("costs summary error", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  const vehicles = data?.vehicles || [];

  return (
    <Card data-testid="vehicles-costs-table">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="w-4 h-4 text-indigo-600" />
            Coût mensuel par véhicule
            <Badge variant="outline" className="text-xs">{vehicles.length}</Badge>
          </CardTitle>
          {vehicles.length > 0 && (
            <div className="flex items-baseline gap-4 text-sm">
              <div>
                <span className="text-slate-500">Total mensuel HT : </span>
                <span className="font-semibold text-slate-900">{fmt(data.grand_total_monthly_ht)}</span>
              </div>
              <div>
                <span className="text-slate-500">Annuel : </span>
                <span className="font-semibold text-slate-900">{fmt(data.grand_total_annual_ht)}</span>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {vehicles.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Aucun véhicule avec des contrats liés pour l&apos;instant.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="text-xs bg-slate-50">
                  <TableHead>Véhicule</TableHead>
                  <TableHead className="text-right">🚗 Leasing</TableHead>
                  <TableHead className="text-right">🛡️ Assurance</TableHead>
                  <TableHead className="text-right">🔧 Entretien</TableHead>
                  <TableHead className="text-right">📄 Autre</TableHead>
                  <TableHead className="text-right font-semibold">Total /mois HT</TableHead>
                  <TableHead className="text-right">Coût /km</TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicles.map((v) => (
                  <TableRow
                    key={v.vehicle_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/fleet/vehicles/${v.vehicle_id}`)}
                    data-testid={`cost-row-${v.vehicle_id}`}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-900">
                          {v.brand} {v.model}
                        </span>
                        <span className="text-xs text-slate-500 font-mono">
                          {v.registration} · {v.internal_code}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-slate-700">
                      {v.monthly_leasing_ht > 0 ? fmt(v.monthly_leasing_ht) : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-slate-700">
                      {v.monthly_insurance_ht > 0 ? fmt(v.monthly_insurance_ht) : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-slate-700">
                      {v.monthly_maintenance_ht > 0 ? fmt(v.monthly_maintenance_ht) : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-slate-700">
                      {v.monthly_other_ht > 0 ? fmt(v.monthly_other_ht) : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">
                      {fmt(v.monthly_total_ht)}
                    </TableCell>
                    <TableCell className="text-right">
                      {v.cost_per_km_ht != null ? (
                        <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200">
                          {fmtDec(v.cost_per_km_ht, 3)}
                        </Badge>
                      ) : (
                        <span className="text-slate-300 text-xs">n/a</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <ExternalLink className="w-4 h-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-slate-400 mt-2">
              💡 Le coût par km est calculé à partir du kilométrage annuel autorisé du contrat de leasing.
              Cliquez sur une ligne pour voir la fiche du véhicule.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
